import axios from 'axios';

const API_BASE_URL = 'https://api.glowytics.com/api';

// Create an axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include brand ID and account ID in each request
apiClient.interceptors.request.use(
  config => {
    const brandId = localStorage.getItem('currentBrandId');
    if (brandId) {
      config.headers['X-Brand-ID'] = brandId;
    }

    // Auto-inject accountId for Google Ads and Meta Ads API calls
    const url = config.url || '';
    let platform = null;
    if (url.includes('/google-ads')) platform = 'GOOGLE_ADS';
    else if (url.includes('/meta-ads')) platform = 'META_ADS';

    if (platform) {
      try {
        const selections = JSON.parse(localStorage.getItem('glowytics_selectedAccounts') || '{}');
        const accountId = selections[platform];
        if (accountId && accountId !== 'all') {
          config.params = config.params || {};
          if (!config.params.accountId) {
            config.params.accountId = accountId;
          }
        }
      } catch { /* ignore parse errors */ }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Request Error:', error.message);
    
    // Log different error types
    if (error.response) {
      // The server responded with a status code outside of 2xx range
      console.log('Error status:', error.response.status);
      console.log('Error data:', error.response.data);
      console.log('Error headers:', error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      // This could be a CORS issue or network problem
      console.log('No response received from the server. Possible CORS or network issue.');
      console.log('Request details:', error.request);
    } else {
      // Something happened in setting up the request
      console.log('Error setting up the request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API service functions
const apiService = {
  // Google Analytics
  getGATrafficSource: async (dateRange) => {
    return apiClient.get('/google-ads/ga-traffic-source', { 
      params: { dateRange }
    });
  },
  
  // Ecommerce data
  getEcommerceData: async (dateRange) => {
    return apiClient.get('/google-ads/ecommerce-data', { 
      params: { dateRange }
    });
  },
  
  // Meta Ads
  getMetaDailySummary: async (dateRange) => {
    return apiClient.get('/meta-ads/daily-summary', { 
      params: { dateRange }
    });
  },
  
  // Google Ads
  getCampaignTypes: async () => {
    return apiClient.get('/google-ads/campaign-types-all');
  },
  
  // Try an alternative approach if CORS is a persistent issue
  proxyRequest: async (endpoint, params = {}, method = 'GET', data = null) => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    try {
      // Try with fetch API as an alternative
      const fetchOptions = {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': localStorage.getItem('currentBrandId') || ''
        }
      };
      
      if (data && (method === 'POST' || method === 'PUT')) {
        fetchOptions.body = JSON.stringify(data);
      }
      
      // Construct URL with params
      const queryParams = new URLSearchParams(params).toString();
      const fullUrl = queryParams ? `${url}?${queryParams}` : url;
      
      const response = await fetch(fullUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Proxy request failed:', error);
      throw error;
    }
  }
};

export default apiService; 